"use client";
import { useEffect, useMemo, useState } from "react";

import type { SurveyQuestion } from "@/types/survey";
import { InputText, RadioGroup } from "@/design-system";
import choiceButtonStyles from "./ChoiceButtonLayout.module.css";
import {
  SELECT_OTHER_LABEL,
  SELECT_OTHER_VALUE,
  isOtherAnswer,
  isSelectOtherEnabled,
  otherValueFromAnswer,
  toOtherAnswer,
} from "@/lib/selectOtherOption";
import {
  getSingleSelectButtonColors,
  getSingleSelectDisplayOption,
  hasCustomSingleSelectButtonColors,
} from "@/lib/singleSelectDisplay";

interface SingleSelectQuestionProps {
  question: SurveyQuestion;
  brandColor?: string;
  isSelected?: (
    question: SurveyQuestion,
    candidate: string | number,
  ) => boolean;
  onAnswerChange?: (question: SurveyQuestion, value: string) => void;
  isPreview?: boolean;
  selectedValue?: string | number;
  onPreviewAnswerChange?: (questionId: string, value: string | number) => void;
  uiStrings?: {
    otherOption: string;
    pleaseSpecify: string;
  };
}

export default function SingleSelectQuestion({
  question,
  brandColor = "var(--color-alias-info)",
  isSelected,
  onAnswerChange,
  isPreview = false,
  selectedValue,
  onPreviewAnswerChange,
  uiStrings,
}: SingleSelectQuestionProps) {
  const choiceItems = useMemo(
    () =>
      (question.choices || []).map((c) => ({
        value: c.value,
        label: c.text,
      })),
    [question.choices],
  );
  const allowOther = isSelectOtherEnabled(question);
  const otherLabel = uiStrings?.otherOption ?? SELECT_OTHER_LABEL;
  const displayOption = getSingleSelectDisplayOption(question);
  const buttonColors = getSingleSelectButtonColors(question);
  const hasCustomButtonColors = hasCustomSingleSelectButtonColors(question);
  const items = useMemo(
    () =>
      allowOther
        ? [
            ...choiceItems,
            { value: SELECT_OTHER_VALUE, label: otherLabel },
          ]
        : choiceItems,
    [allowOther, choiceItems, otherLabel],
  );

  const rawSelected = selectedValue == null ? "" : String(selectedValue);
  const resolvedSelectedValue = useMemo(() => {
    if (allowOther && isOtherAnswer(rawSelected)) return SELECT_OTHER_VALUE;

    if (isPreview) {
      const selectedFromValue = items.find(
        (item) => String(item.value) === rawSelected,
      )?.value;
      if (selectedFromValue != null) return selectedFromValue;

      const selectedFromLabel = items.find(
        (item) => String(item.label) === rawSelected,
      )?.value;
      return selectedFromLabel ?? null;
    }

    const selectedFromLabel = items.find(
      (item) =>
        String(item.label) === rawSelected &&
        String(item.value) !== SELECT_OTHER_VALUE,
    )?.value;
    if (selectedFromLabel != null) return selectedFromLabel;

    const selectedFromChoices = items
      .filter((item) => String(item.value) !== SELECT_OTHER_VALUE)
      .find((item) => (isSelected ? isSelected(question, item.value) : false))
      ?.value;
    return selectedFromChoices ?? null;
  }, [allowOther, isPreview, isSelected, items, question, rawSelected]);

  const [selectedRadioOverride, setSelectedRadioOverride] = useState<
    string | null
  >(null);
  const [otherInputValue, setOtherInputValue] = useState(
    otherValueFromAnswer(rawSelected),
  );

  useEffect(() => {
    if (resolvedSelectedValue === SELECT_OTHER_VALUE) {
      setSelectedRadioOverride(SELECT_OTHER_VALUE);
      setOtherInputValue(otherValueFromAnswer(rawSelected));
      return;
    }
    if (resolvedSelectedValue == null || resolvedSelectedValue === "") {
      setSelectedRadioOverride(null);
    } else {
      setSelectedRadioOverride(String(resolvedSelectedValue));
    }
    setOtherInputValue("");
  }, [rawSelected, resolvedSelectedValue]);

  const selectedRadioValue =
    selectedRadioOverride === null || selectedRadioOverride === ""
      ? null
      : selectedRadioOverride;

  const commitValue = (value: string) => {
    if (isPreview && onPreviewAnswerChange) {
      onPreviewAnswerChange(question.id, value);
      return;
    }
    if (onAnswerChange) {
      onAnswerChange(question, value);
    }
  };

  const handleValueChangeUnknown = (next: unknown) => {
    const v =
      next === null || typeof next === "undefined" ? null : String(next);
    if (v === SELECT_OTHER_VALUE && allowOther) {
      setSelectedRadioOverride(SELECT_OTHER_VALUE);
      commitValue(toOtherAnswer(otherInputValue, { allowBlank: true }));
      return;
    }

    setOtherInputValue("");
    setSelectedRadioOverride(v);
    commitValue(String(v ?? ""));
  };

  const handleOtherTextChange = (event: { target: { value: string } }) => {
    const next = event.target.value;
    setOtherInputValue(next);
    commitValue(toOtherAnswer(next, { allowBlank: true }));
  };

  const renderButtonOptions = () => {
    const columnCount = Math.max(items.length, 1);
    return (
      <div
        role="radiogroup"
        aria-label={question.text || "Single select options"}
        className={`${choiceButtonStyles.buttonGrid} ${choiceButtonStyles.singleSelectButtonGrid}`}
        style={{
          gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
        }}
      >
        {items.map((item) => {
          const itemValue = String(item.value);
          const selected = selectedRadioValue === itemValue;
          // Honor explicit per-question button colors when configured;
          // otherwise fall back to the survey theme/brand styling that
          // matches the matrix and ranking number buttons.
          const colorStyle = hasCustomButtonColors
            ? {
                backgroundColor: buttonColors.backgroundColor,
                border: `1px solid ${buttonColors.borderColor}`,
                boxShadow: selected
                  ? `inset 0 0 0 1px ${buttonColors.borderColor}`
                  : "none",
                color: buttonColors.textColor,
                fontWeight: selected ? 700 : 600,
              }
            : {
                backgroundColor: selected ? brandColor : "transparent",
                border: `1px solid ${
                  selected ? brandColor : "var(--color-alias-neutral-light)"
                }`,
                boxShadow: "none",
                color: selected
                  ? "var(--color-alias-foreground-on-color)"
                  : "var(--sl-text, var(--color-alias-neutral-darkest))",
                fontWeight: 600,
              };
          return (
            <button
              key={itemValue}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => handleValueChangeUnknown(itemValue)}
              className={`${choiceButtonStyles.choiceButton} ${choiceButtonStyles.singleSelectButton}`}
              style={{
                alignItems: "center",
                borderRadius: "var(--border-radius-md, 6px)",
                cursor: "pointer",
                display: "flex",
                fontFamily: "inherit",
                justifyContent: "center",
                lineHeight: 1.25,
                margin: 0,
                minHeight: "40px",
                minWidth: 0,
                overflowWrap: "break-word",
                textAlign: "center",
                transition:
                  "background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
                whiteSpace: "normal",
                wordBreak: "normal",
                ...colorStyle,
              }}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="mb-4">
      <div className="text-sm font-medium text-left" style={{ color: "var(--sl-text, var(--color-alias-neutral-darkest))", marginBottom: "calc(var(--spacing) * 4)" }}>
        {question.text}
      </div>
      {displayOption === "buttons" ? (
        renderButtonOptions()
      ) : (
        <RadioGroup
          label=""
          items={items}
          value={selectedRadioValue}
          onValueChange={handleValueChangeUnknown}
          orientation="vertical"
        />
      )}
      {selectedRadioValue === SELECT_OTHER_VALUE ? (
        <div className="mt-2">
          <InputText
            type="text"
            placeholder={uiStrings?.pleaseSpecify ?? "Please specify"}
            value={otherInputValue}
            onChange={handleOtherTextChange}
            fullWidth
          />
        </div>
      ) : null}
    </div>
  );
}
